import { useAppContext } from '@ifixit/app/hooks';
import { useAuthenticatedUser } from '@ifixit/auth-sdk/client';
import type { User } from '@ifixit/auth-sdk';
import { captureException, SentryError } from '@ifixit/sentry';
import {
   type AttributeInput,
   type CartInput,
   type CartLineInput,
   type CartWarningFieldsFragment,
   type CountryCode,
   type LanguageCode,
   type StorefrontClient,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useCallback } from 'react';
import { useGetCustomerAccessToken } from '../use-get-customer-access-token';
import { getSyncedAttributes } from '../../helpers/attributes';
import { wasCustomerTokenRejected } from '../../helpers/customer-token-errors';
import { clearStoredCustomerAccessToken, updateCartId } from '../../helpers/storage';

type UseCreateCartProps = {
   lines?: CartLineInput[];
   country: CountryCode;
   language: LanguageCode;
};
export function useCreateCart() {
   const { isOnMobileApp } = useAppContext();
   const userQueryResult = useAuthenticatedUser();
   const { client, storeCode } = useShopifyStorefrontClient();
   const getCustomerAccessToken = useGetCustomerAccessToken();
   return useCallback(
      async ({ country, lines, language }: UseCreateCartProps) => {
         const user = userQueryResult.data ?? null;
         const { attributes } = getSyncedAttributes({ cart: null, isOnMobileApp });
         const { cartCreate, cartUser } = await createCartWithBuyerIdentity({
            attributes,
            client,
            country,
            getCustomerAccessToken,
            lines,
            language,
            storeCode,
            user,
         });

         const userErrors = cartCreate?.userErrors ?? [];
         const validationErrors: string[] = [];
         const warnings: CartWarningFieldsFragment[] = [];
         const cart = cartCreate?.cart;
         const cartId = cart?.id;

         if (!cart || !cartId) {
            throw new SentryError('Failed to create cart', { extra: { lines, userErrors } });
         }

         updateCartId({ cartId, storeCode, userId: cartUser?.id ?? null });
         return { cart, userErrors, validationErrors, warnings };
      },
      [client, getCustomerAccessToken, isOnMobileApp, storeCode, userQueryResult]
   );
}

type CreateCartProps = {
   attributes: AttributeInput[];
   client: StorefrontClient;
   country: CountryCode;
   getCustomerAccessToken: ReturnType<typeof useGetCustomerAccessToken>;
   lines: UseCreateCartProps['lines'];
   language: LanguageCode;
   previousTokenRejected?: boolean;
   user: User | null;
};

export async function createCartWithBuyerIdentity({
   storeCode,
   user,
   ...cartProps
}: CreateCartProps & { storeCode: string }) {
   const first = await createCart({ ...cartProps, user });
   if (!user || !wasCustomerTokenRejected(first.cartCreate, first.sentToken)) {
      return { cartCreate: first.cartCreate, cartUser: user };
   }

   clearStoredCustomerAccessToken({ storeCode, user });
   const withFreshToken = await createCart({
      ...cartProps,
      previousTokenRejected: true,
      user,
   });
   if (!wasCustomerTokenRejected(withFreshToken.cartCreate, withFreshToken.sentToken)) {
      return { cartCreate: withFreshToken.cartCreate, cartUser: user };
   }

   captureException(
      new SentryError('Shopify rejected a fresh customer access token', {
         extra: { storeCode, userErrors: withFreshToken.cartCreate?.userErrors },
      })
   );
   clearStoredCustomerAccessToken({ storeCode, user });
   const anonymous = await createCart({ ...cartProps, user: null });
   return { cartCreate: anonymous.cartCreate, cartUser: null };
}

async function createCart({
   attributes,
   client,
   country,
   getCustomerAccessToken,
   lines,
   language,
   previousTokenRejected,
   user,
}: CreateCartProps) {
   const customerAccessToken = user
      ? await getCustomerAccessToken(user, { previousTokenRejected })
      : null;
   const sentToken = customerAccessToken?.accessToken;
   const input: CartInput = {
      attributes,
      buyerIdentity: { customerAccessToken: sentToken },
      lines,
   };
   const { cartCreate } = await client.createCart({ input, country, language });
   return { cartCreate, sentToken };
}
